import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import React from 'react';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import GroupService from '../../../services/group.service';
import Group from '../../../@types/group';
import {ResourceType} from '../../../@types/resource-type';
import {toast} from 'react-toastify';
import DotdotdotButton from '../../form-components/DotdotdotButton.component';
import DeleteResource from '../modals/DeleteResource.component';
import Box from '@mui/material/Box';
import DuplicateResource from '../modals/DuplicateResource.component';
import MoveResource from '../modals/MoveResource.component';
import {getTreeItemDescendantIds} from '../../../utils/resource-utils';
import {excludeGroupAndDescendants} from '../../../utils/group-utils';
import LeaveGroupModal from '../modals/LeaveGroup.component';
import authService from '../../../services/auth.service';
import {ResourceAction} from '../../../@types/resource-action';

export default function GroupActionsMenu(props: {
    group: Group
}): React.ReactElement {
    const [confirmDeleteGroup, setConfirmDeleteGroup] = React.useState<boolean>(false);
    const [confirmDuplicateGroup, setConfirmDuplicateGroup] = React.useState<boolean>(false);
    const [confirmMoveGroup, setConfirmMoveGroup] = React.useState<boolean>(false);
    const [confirmLeaveGroup, setConfirmLeaveGroup] = React.useState<boolean>(false);

    const { setCurrentWorkspaceObject,
        setDeletedWorkspaceObject,
        setWorkspaceObjects, workspaceObjects, getAdditionalResource, setCurrentAction,
        registerPendingGroupDelete  } = useWorkspaceContext();
    const { group } = props;
 
    function onEdit(): void {
        setCurrentAction(ResourceAction.MEMBERS);
        setCurrentWorkspaceObject({ objectType: ResourceType.GROUPS, object: group });
    }

    function onDelete(): void {
        setConfirmDeleteGroup(true);
    }

    function onDuplicate(): void {
        setConfirmDuplicateGroup(true);
    }

    function onMove(): void {
        setConfirmMoveGroup(true);
    }

    function onLeave(): void {
        setConfirmLeaveGroup(true);
    }

    function onNewGroup(): void {
        let id = group.id;
        const newGroup = new Group();
        newGroup.setParent(id);
        setCurrentWorkspaceObject({ objectType: ResourceType.GROUPS, object: newGroup });
    }

    return (
        <Box tabIndex={0}  onClick={(event: any): void => {
            event.stopPropagation();
        }}>
            <DotdotdotButton>     
                <MenuItem id='edit' onClick={onEdit} disabled={!group.canEdit()}>{authService.isSuperUser() ? 'Edit' : 'Members'}</MenuItem>
                <MenuItem id='new' onClick={onNewGroup} disabled={!group.canEdit()}>New Sub Working Area</MenuItem>
                <MenuItem id='duplicate' onClick={onDuplicate} disabled={!group.canEdit() || (!authService.isSuperUser() && !group.parent)}>Duplicate</MenuItem>
                <MenuItem id='move' onClick={onMove} disabled={!group.canEdit()}>Move</MenuItem>
                <Divider />
                <MenuItem id='leave' onClick={onLeave} disabled={!group.canLeave()}>Leave Working Area</MenuItem>
                <MenuItem id='delete' onClick={onDelete} disabled={!group.canEdit() || (!authService.isSuperUser() && !group.parent)}>Delete</MenuItem>    
            </DotdotdotButton>
            <LeaveGroupModal
                leaveGroup={confirmLeaveGroup}
                group={group}
                onConfirm={(): void => {
                    setConfirmLeaveGroup(false);
                    let childrenIds = getTreeItemDescendantIds(group,
                        ResourceType.GROUPS,
                        workspaceObjects?.resources ?? []);
                    GroupService.leaveGroupById(group).then(() => {
                        setDeletedWorkspaceObject({
                            objectType: ResourceType.GROUPS,
                            id: group.id!,
                            children: childrenIds
                        });
                        setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                    }).catch((error) => {
                        toast.error(error.message);
                    }
                    );
                }}
                onClose={(): void => {
                    setConfirmLeaveGroup(false);
                }}
            />
            <DeleteResource
                open={confirmDeleteGroup}
                resourceType={ResourceType.GROUPS}
                resourceName={group.name}
                subResources={'sub working areas and resources'}
                handleDelete={(): void => {
                    let childrenIds = getTreeItemDescendantIds(group,
                        ResourceType.GROUPS,
                        workspaceObjects?.resources ?? []);
                    // Register before calling the API so the SSE handler can close any tabs
                    // the user opens during the window between enqueue and actual deletion.
                    registerPendingGroupDelete(group.id!, group.name, childrenIds);
                    GroupService.deleteGroupById(group.id!).then(() => {
                        setDeletedWorkspaceObject({
                            objectType: ResourceType.GROUPS,
                            id: group.id!,
                            children: childrenIds
                        });
                        setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                    }).catch((error) => {
                        toast.error(error.message);
                    }).finally(() => {
                        setConfirmDeleteGroup(false);
                    });

                }}
                onClose={(): void => {
                    setConfirmDeleteGroup(false);
                }}
            />
            <DuplicateResource
                open={confirmDuplicateGroup}
                title={'Duplicate Working Area - ' + group.name}
                onClose={(): void => { setConfirmDuplicateGroup(false); }}
                onConfirm={
                    (id: number): void => {
                        let targetGroupId = id > 0 ? id : undefined;
                        let sourceGroupId = group.id;
                        setConfirmDuplicateGroup(false);
                        GroupService.duplicateGroup(sourceGroupId!, targetGroupId).then((response) => {
                            toast.info(response.data.status + ' ' + response.data.name);
                        }).catch((error) => {
                            toast.error(error.message);
                        });
                    }
                }
                object={{objectType: ResourceType.GROUPS, object: group}}
                data={excludeGroupAndDescendants(getAdditionalResource(ResourceType.GROUPS), group, workspaceObjects)}
            />
            <MoveResource
                open={confirmMoveGroup}
                title={'Move Working Area - ' + group.name}
                onClose={(): void => { setConfirmMoveGroup(false); }}
                onConfirm={(targetParentId: number | null): void => {
                    setConfirmMoveGroup(false);
                    GroupService.moveGroup(group.id!, targetParentId).then(() => {
                        setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS });
                    }).catch((error) => {
                        toast.error('Error moving working area - ' + error.message);
                    });
                }}
                object={{ objectType: ResourceType.GROUPS, object: group }}
                data={excludeGroupAndDescendants(getAdditionalResource(ResourceType.GROUPS), group, workspaceObjects)}
            />


        </Box>
    );
}


